import {
  Component,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { useAuthContext } from "@acto/web-common/auth";
import { usePlatformName } from "@acto/taskpane/core/platform/PlatformNameContext";
import { SessionTelemetry, isTelemetryConfigured } from "./sessionTelemetry";

const SessionTelemetryContext = createContext<SessionTelemetry | null>(null);

interface ErrorBoundaryProps extends PropsWithChildren {
  onCrash: (error: Error, info: ErrorInfo) => void;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * React error boundary that reports render crashes to session telemetry.
 * Kept in-file because it's only useful in concert with the provider.
 */
class TelemetryErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onCrash(error, info);
  }

  render(): ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

interface SessionTelemetryProviderProps extends PropsWithChildren {
  /**
   * Rendered in place of children when a React render crash happens. Kept
   * minimal on purpose — the widget host environment (Office, Salesforce)
   * handles the outer shell.
   */
  fallback?: ReactNode;
}

/**
 * Bootstraps session reliability telemetry for the widget.
 *
 *   - Emits apps.widget.session_started once on mount (tagged with tenant +
 *     platform).
 *   - Registers window.error and window.unhandledrejection listeners that
 *     emit apps.widget.session_crashed (de-duplicated to one per session).
 *   - Wraps children in a React error boundary that emits the same counter
 *     when a render crashes.
 *   - Each crash source also ships the error's message + stack as an OTLP
 *     log record via recordError (platform-gated and capped per session).
 *
 * Safe to mount when telemetry env vars are unset — emits become no-ops.
 */
export function SessionTelemetryProvider({ children, fallback = null }: SessionTelemetryProviderProps): ReactNode {
  const platform = usePlatformName();
  const loggedMissingConfigRef = useRef(false);

  // The auth context does not exist at this mount point (the router creates
  // it further down), so reading it here would silently yield the module
  // default forever. Labels start as placeholders and arrive through the
  // instance's set* methods.
  const telemetry = useMemo(() => {
    return new SessionTelemetry({
      tenant: "unknown",
      platform: platform ?? "unknown",
    });
  }, [platform]);

  useEffect(() => {
    if (!isTelemetryConfigured() && !loggedMissingConfigRef.current) {
      console.debug(
        "[session-telemetry] VITE_SIGNOZ_OTLP_ENDPOINT or VITE_SIGNOZ_INGESTION_KEY unset, metrics disabled",
      );
      loggedMissingConfigRef.current = true;
    }

    void telemetry.recordSessionStart();

    const onError = (event: ErrorEvent) => {
      void telemetry.recordSessionCrash("error");
      void telemetry.recordError("error", event.error ?? event.message);
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      void telemetry.recordSessionCrash("unhandledrejection");
      void telemetry.recordError("unhandledrejection", event.reason);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, [telemetry]);

  return (
    <SessionTelemetryContext.Provider value={telemetry}>
      <TelemetryErrorBoundary
        onCrash={(error) => {
          void telemetry.recordSessionCrash("react");
          void telemetry.recordError("react", error);
          console.error("[session-telemetry] React render crash", error);
        }}
        fallback={fallback}
      >
        {children}
      </TelemetryErrorBoundary>
    </SessionTelemetryContext.Provider>
  );
}

/**
 * Access the active session telemetry instance. Returns null if the
 * provider is not mounted. Primarily used by tests; feature code does not
 * usually need this (the provider wires everything automatically).
 */
export function useSessionTelemetry(): SessionTelemetry | null {
  return useContext(SessionTelemetryContext);
}

/**
 * Syncs tenant and user into the session telemetry and counts the session once
 * the tenant resolves. Under the default auth context (no auth provider above
 * it) the tenant never resolves and nothing is recorded.
 */
export function SessionTelemetryContextSync(): null {
  const telemetry = useSessionTelemetry();
  const { user, tenantName } = useAuthContext();

  // Prefer tenantName (Frontegg display name, e.g. "Boltze GmbH") so the
  // scorecard dashboard can filter demo vs real tenants without leaking
  // UUIDs into metric cardinality.
  // Use || not ?? because AuthContext defaults tenantName to "" (not null)
  // when no active tenant is selected; ?? would keep the empty string.
  const tenantLabel = tenantName || user?.tenantId || "unknown";
  const userEmail = user?.email;

  useEffect(() => {
    if (!telemetry || tenantLabel === "unknown") return;
    telemetry.setTenant(tenantLabel);
    telemetry.setUser(userEmail);
    void telemetry.recordSessionAuthenticated();
  }, [telemetry, tenantLabel, userEmail]);

  return null;
}
